/**
 * Small read-only hooks for masters that back a single dropdown.
 *
 * These exist because the same list was being hardcoded in several components at once,
 * and the copies drifted apart:
 *
 *   PWD Class      ContractorForm offered CLASS_I..CLASS_V; ContractorSelector offered
 *                  the same five PLUS 'Special' — and as display labels, not codes, so
 *                  its filter compared 'Class I' against a stored 'CLASS_I' and matched
 *                  nothing.
 *   Designation    three components, three different lists: BillEntry and the
 *                  measurements page had five grades, MeasurementItemProfile only four
 *                  (no Assistant Executive Engineer).
 *
 * Every one of those is now a row in Setup. A component that needs the list calls the
 * hook rather than restating it, so there is one place to change and no way for two
 * screens to disagree.
 *
 * Failure is non-fatal and returns an empty list: a master that cannot be reached must
 * not white-screen the form around it. Callers show their own "not configured" hint.
 */
import { useState, useEffect } from 'react';
import { mastersApi } from '../services/wmsService';

export interface PwdClass {
  pwd_cls_id: number;
  pwd_cls_cd: string;
  pwd_cls_nm: string;
  min_at?: number | null;
  max_at?: number | null;
}

export interface ContractorStatus {
  cntrctr_sts_id: number;
  cntrctr_sts_cd: string;
  cntrctr_sts_nm: string;
}

export interface EstimateType {
  estmt_typ_id: number;
  estmt_typ_cd: string;
  estmt_typ_nm: string;
  estmt_typ_axs_cd?: string | null;
}

export interface FieldDesignation {
  dsgn_id: number;
  dsgn_cd: string;
  dsgn_nm: string;
  dsgn_lvl_nu?: number | null;
}

function useMaster<T>(fetcher: () => Promise<any>, label: string): T[] {
  const [rows, setRows] = useState<T[]>([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetcher();
        const list = Array.isArray(res) ? res : (res?.data ?? []);
        if (alive && Array.isArray(list)) setRows(list);
      } catch (e) {
        console.error(`Error loading ${label}:`, e);
      }
    })();
    return () => { alive = false; };
    // The fetcher is a stable module-level method; re-running on identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return rows;
}

/** PWD registration classes from dswms_pwd_cls_lst_t (Setup -> PWD Classes). */
export const usePwdClasses = () =>
  useMaster<PwdClass>(() => mastersApi.getPwdClasses(true), 'PWD classes');

/**
 * Field designations from dswms_dsgn_lst_t (Setup -> Field Designations).
 *
 * Not the ERP-wide designation master: that one holds 49,112 rows spanning every
 * department in the ERP and is unusable as a field dropdown.
 */
export const useFieldDesignations = () =>
  useMaster<FieldDesignation>(() => mastersApi.getWmsDesignations(true), 'field designations');

/** Contractor registration statuses from dswms_cntrctr_sts_lst_t (Setup -> Contractor Statuses). */
export const useContractorStatuses = () =>
  useMaster<ContractorStatus>(() => mastersApi.getContractorStatuses(true), 'contractor statuses');

/**
 * Estimate types from dswms_estmt_typ_lst_t (Setup -> Estimate Types).
 *
 * The master holds two axes in estmt_typ_axs_cd — NATURE (Original / Revised /
 * Supplementary) and DETAIL (Preliminary / Detailed). Pass an axis to get one of them;
 * a form asking "is this a revision?" must not be offered "Detailed".
 */
export const useEstimateTypes = (axis?: string) => {
  const all = useMaster<EstimateType>(() => mastersApi.getEstimateTypes(true), 'estimate types');
  return axis ? all.filter((t) => t.estmt_typ_axs_cd === axis) : all;
};
